import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { STRINGS, SUBJECT_NAMES } from "@/lib/i18n";
import { api, getUserId } from "@/lib/api";

const AppContext = createContext(null);

export function AppProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("lo_lang") || "en");
    const [theme, setTheme] = useState(() => localStorage.getItem("lo_theme") || "light");
    const [bookmarks, setBookmarks] = useState([]);
    const userId = getUserId();

    useEffect(() => {
        const root = document.documentElement;
        if (theme === "dark") root.classList.add("dark");
        else root.classList.remove("dark");
        localStorage.setItem("lo_theme", theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("lo_lang", lang);
    }, [lang]);

    const refreshBookmarks = useCallback(async () => {
        try {
            const res = await api.listBookmarks(userId);
            setBookmarks(res.items.map((i) => i.code));
        } catch (e) {
            console.warn("bookmark refresh failed", e);
        }
    }, [userId]);

    useEffect(() => { refreshBookmarks(); }, [refreshBookmarks]);

    const toggleBookmark = useCallback(async (code) => {
        if (bookmarks.includes(code)) {
            await api.removeBookmark(code, userId);
            setBookmarks((b) => b.filter((c) => c !== code));
        } else {
            await api.addBookmark(code, userId);
            setBookmarks((b) => [...b, code]);
        }
    }, [bookmarks, userId]);

    const t = STRINGS[lang];
    const subjectLabel = (s) => SUBJECT_NAMES[lang][s] || s;

    return (
        <AppContext.Provider value={{
            lang, setLang,
            theme, setTheme,
            t, subjectLabel,
            bookmarks, toggleBookmark, refreshBookmarks,
            userId,
        }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used within AppProvider");
    return ctx;
}
